import React, { useEffect, useMemo, useState } from 'react';
import useSpeechToText from 'react-hook-speech-to-text';
// @ts-ignore
import styles from './AudioToText.module.scss';
import { Position } from '../../Enums/Position.enum';
import { joinClasses } from '../../../../Utils/className.util';
import BaseModal from '../BaseModal/BaseModal';
import Text from '../../../../Components/Base/Text/Text';
import { isSafari } from 'react-device-detect';
import Tooltip from '../../../../Components/Popup/Tooltip';

interface Props {
	value?: string;
	onCapture: (value: string) => void;
	position: Position;
}

const AudioToText: React.FC<Props> = ({ value, onCapture, position }) => {
	const [isUnsupportedBrowser, setIsUnsupportedBrowser] = useState(false);
	const { isRecording, results, startSpeechToText, stopSpeechToText, error } =
		useSpeechToText({
			continuous: true,
			useLegacyResults: true,
		});

	useEffect(() => {
		const texts: string[] = [];
		if (value) {
			texts.push(value);
		}
		if (results.length > 0) {
			const lastSpokenText = results[results.length - 1];
			if (typeof lastSpokenText === 'string') {
				texts.push(lastSpokenText);
			}
		}
		onCapture(texts.join(' '));
	}, [results]);

	const handleToggleRecording = () => {
		if (error) {
			setIsUnsupportedBrowser(true);
		}
		if (isRecording) {
			stopSpeechToText();
		} else {
			startSpeechToText();
		}
	};

	const tooltipMessage: string = useMemo(() => {
		if (isSafari) {
			return 'component.audioToText.safariTooltip';
		}
		return 'component.audioToText.defaultTooltip';
	}, [isSafari]);

	const content = (
		<div className={styles.content} onClick={handleToggleRecording}>
			{isRecording && (
				<>
					<span className={styles.ring1} />
					<span className={styles.ring2} />
					<span className={styles.ring3} />
				</>
			)}
			<img src='/dist/icons/v2/microphone-solid.svg' alt='microphone icon' />
		</div>
	);

	return (
		<>
			<div
				className={joinClasses([
					styles.wrapper,
					position === Position.LEFT ? styles.pleft : styles.pright,
				])}>
				<Tooltip
					position='top center'
					trigger={content}
					isOpen={isRecording}
					disableHover>
					<Text tx={tooltipMessage} />
				</Tooltip>
			</div>

			{isUnsupportedBrowser && (
				<BaseModal
					isOpen
					title='component.audioToText.unsupportedBrowser'
					onClose={() => setIsUnsupportedBrowser(false)}>
					<div className={styles.modalContent}>
						<Text
							className={styles.message}
							tx='component.audioToText.unsupportedBrowserMessage'
						/>
						<Text
							className='mt-3'
							tx='component.audioToText.supportedBrowsers'
						/>
						<ul>
							<li>Google Chrome</li>
							<li>Safari</li>
						</ul>
					</div>
				</BaseModal>
			)}
		</>
	);
};

export default AudioToText;
